import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import '../../style.css';

const Main = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [textQuery, setTextQuery] = useState("");
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
            setMatches([]);
            setError(null);
        }
    };

    const handleTextChange = (e) => {
        setTextQuery(e.target.value);
    };

    const clearImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleViewImage = async (url) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (err) {
            console.error('Error opening image:', err);
            window.open(url, '_blank'); // Fallback
        }
    };

    const findMatches = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage && !textQuery.trim()) return;

        setError(null);
        setLoading(true);
        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        if (textQuery.trim()) {
            formData.append('text_query', textQuery);
        }

        try {
            const response = await fetch('/api/find_match', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to fetch matches');
            }

            const data = await response.json();
            setMatches(data.points);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const findMatchesWithXimilar = async (e) => {
        if (e) e.preventDefault();
        if (!selectedImage && !textQuery.trim()) return;

        setError(null);
        setLoading(true);
        setMatches([]);

        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        if (textQuery.trim()) {
            formData.append('text_query', textQuery);
        }

        try {
            const response = await fetch('/api/ximilar_search', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || 'Failed to fetch matches from Ximilar');
            }

            const data = await response.json();
            const mappedResults = (data.results || []).map((rec) => ({
                payload: {
                    image_name: rec.image_name,
                    image_path: rec.path,
                }
            }));
            setMatches(mappedResults);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    return (
        <Container fluid className="p-4">
            <Row className="g-4">
                <Col lg={4} md={5}>
                    <Card className="advanced-search-card py-4">
                        <div className="text-center mb-4">
                            <h4 className="fw-bold">Visual Search</h4>
                            <p className="text-muted small mb-0">Search by text, image, or both together (Ximilar).</p>
                        </div>

                        <div className="search-container shadow-sm p-2 mb-3 bg-white rounded-pill d-flex align-items-center border">
                            <input 
                                type="text" 
                                className="border-0 outline-none bg-transparent px-3 py-2 flex-grow-1" 
                                style={{ minWidth: 0 }}
                                placeholder="Describe jewelry... (e.g. 'diamond ring')" 
                                value={textQuery}
                                onChange={handleTextChange}
                                onKeyDown={(e) => e.key === 'Enter' && findMatchesWithXimilar()}
                                disabled={loading}
                            />
                            <Button 
                                className="btn-primary-custom rounded-pill px-3"
                                onClick={findMatchesWithXimilar}
                                disabled={loading || (!textQuery.trim() && !selectedImage)}>
                                {loading && textQuery.trim() && !selectedImage ? <Spinner animation="border" size="sm"/> : 'Search'}
                            </Button>
                        </div>

                        <div className="upload-dropzone">
                            <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleImageChange} 
                                disabled={loading}
                            />
                            <div className="py-2 text-center">
                                {imagePreview ? (
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px' }} 
                                        />
                                        <button
                                            onClick={(e) => { e.preventDefault(); clearImage(); }}
                                            disabled={loading}
                                            style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                right: '-10px',
                                                background: '#767575',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                zIndex: 10
                                            }}
                                            title="Remove Image"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                     <div className="text-muted">
                                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>+</div>
                                        <p className="mb-0">Click or drag image to upload</p>
                                        <small>JPG, PNG or WEBP</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        {selectedImage && (
                            <div className="text-center mt-3">
                                <Button 
                                    onClick={findMatchesWithXimilar} 
                                    disabled={loading}
                                    className="btn-primary-custom px-4 rounded-2">
                                    {loading ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Searching...
                                        </>
                                    ) : 'Find Matches'}
                                </Button>
                            </div>
                        )}
                    </Card>
                </Col>

                <Col lg={8} md={7}>
                    {matches.length > 0 ? (
                        <div>
                            <h4 className="section-title mb-4">Search Results ({matches.length})</h4>
                            <Row xs={1} sm={2} md={2} lg={3} className="g-4">
                                {matches.map((match, index) => {
                                    const imgName = match.payload?.image_name;
                                    return (
                                        <Col key={index}>
                                            <Card className="match-card h-100 shadow-sm p-2">
                                                <Card.Img variant="top" style={{ cursor: 'pointer' }} src={`/api/Images/${imgName}`} onClick={()=>handleViewImage(`/api/Images/${imgName}`)}/>
                                                <Card.Body className="p-2">
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ) : (
                        <div className="text-center h-100 py-5 text-muted bg-light rounded-3 border d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                            <i className="bi bi-search fs-1 mb-2 text-secondary"></i>
                            <p className="mb-0">
                                {loading ? 'Searching for matches...' : (selectedImage || textQuery) ? 'No matches found yet. Try adjusting your search.' : 'Search results will appear here.'}
                            </p>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default Main;
